import React from 'react';
import { fulfillPaymentRequest, revertPaymentRequestPayment } from '../services/api';
import { showSuccess, showError } from './ToastNotifications';
import { revertPaymentConfirmText, taskTranches } from '../utils/paymentTask';
import { formatCurrency } from '../utils/helpers';
import FulfillPaymentModal from './FulfillPaymentModal';
import './UploadReceiptModal.css';

// Finance fulfils a CUSTOMER-BASED payment request. This is now a THIN WRAPPER
// over the shared <FulfillPaymentModal> — the whole body (balance-aware bank/cash
// picker, customer-front picker, multi-slip staging, Review → Confirm → prepare,
// receipt paste/upload, proof image column) lives there so the payment_request
// and platform_order flows can never visually diverge. This file only injects the
// payment_request-specific text + API wiring:
//   - fulfillPaymentRequest submits every staged slip in ONE atomic transaction
//     (one snapshot, one grouped tx-history row); it PREPARES only (money moves
//     later at "Mark complete" in Task Tracking).
//   - revertPaymentRequestPayment cancels a still-PREPARED tranche (no money moved)
//     or reverses a completed one.
// onSuccess fires on FULL completion (parent closes); onPartial after a partial
// submission (parent refreshes, modal stays open).
function FulfillPaymentRequestModal({ isOpen, onClose, task, onSuccess, onPartial }) {
  if (!isOpen || !task) return null;

  // Seed the "Payments already on this request" list synchronously from the
  // tranches the task carries, in the shared modal's {id,amount,source_type,
  // status,url,label} shape.
  const seeded = taskTranches(task).map((tr) => ({
    id: tr.id,
    amount: parseFloat(tr.amount || 0),
    source_type: tr.source_type,
    status: tr.status,
    url: tr.url || tr.receipt_url || null,
    label: tr.source_type === 'customer' ? 'customer front' : 'bank/cash',
  }));

  return (
    <FulfillPaymentModal
      isOpen={isOpen}
      onClose={onClose}
      task={task}
      title="Fulfil Payment Request"
      renderPrimary={(t) => <><strong>Customer:</strong> {t.customer_name || '—'}</>}
      renderTotals={({ requestedTotal, paidTotal, remaining, fmt }) => (
        <>
          <strong>Requested:</strong> {fmt(requestedTotal)}
          {paidTotal > 0 && (
            <span style={{ marginLeft: 10, color: '#b45309', fontWeight: 600 }}>
              Paid {fmt(paidTotal)} · Remaining {fmt(remaining)}
            </span>
          )}
        </>
      )}
      getPayeeText={(t) => (t.account_name || t.description || '').toString()}
      excludeFundCustomerId={task.customer_id}
      paymentsListLabel="Payments already on this request"
      descriptionHelp={<>Pre-filled from the payee details above. Shown on <strong>{task.customer_name || 'the customer'}</strong>'s ledger and the paying account's transaction history.</>}
      renderPaymentCancel={(p, { onCancel, submitting }) => (
        p.id ? (
          <button type="button" className="btn btn-small btn-warning" onClick={onCancel} disabled={submitting}
            title={p.status === 'prepared'
              ? 'Cancel this prepared payment (no money has moved). Mark it complete from Task Tracking once the bank debits.'
              : 'Reverse this completed payment'}>
            {p.status === 'prepared' ? 'Cancel' : 'Revert'}
          </button>
        ) : null
      )}
      fullyPreparedWarning={<><strong>⚠️ Already prepared — do not pay again.</strong> This request is fully prepared and awaiting bank confirmation. The payee details are hidden to prevent a double payment. Once the bank has debited, <strong>mark it complete in Task Tracking</strong>. If it was never sent, cancel the prepared payment below.</>}
      confirmReceiptLine={<>The receipt is saved now so sales can forward it to <strong>{task.customer_name}</strong>. <strong>No balance changes and no snapshot</strong> until you confirm the bank has debited via <strong>Mark complete</strong> in Task Tracking.</>}
      preparedToast={(prepared) => `Prepared ${formatCurrency(prepared)} — forward the receipt to the customer, then Mark complete once the bank debits.`}
      prepareErrorMessage="Failed to fulfil payment request"
      initialPaidTotal={parseFloat(task.paid_total || 0)}
      initialPayments={seeded}
      onPrepare={async (slips) => {
        const result = await fulfillPaymentRequest(task.id, slips.map((s) => ({
          receiptFile: s.receiptFile,
          amount: s.amount,
          source_type: s.source_type,
          debited_bank_account_id: s.debited_bank_account_id,
          debited_customer_id: s.debited_customer_id,
          description: s.description,
        })));
        // Atomic submit → don't refresh the tranche list here (matches the
        // long-standing payment_request behaviour); just report the new totals.
        return {
          paidTotal: parseFloat(result.paid_total),
          remaining: parseFloat(result.remaining_amount || 0),
          preparedAmount: result.prepared_amount ?? result.payment_amount,
          balanceWarnings: result.balance_warnings || [], // 2026-10-09 prepare-time reminders
        };
      }}
      onCancelPayment={async (p) => {
        const prepared = p.status === 'prepared';
        // Shared wording with Task Tracking + the tx-history payout row
        // (utils/paymentTask.revertPaymentConfirmText) — this surface used to
        // describe the same act in its own, thinner words.
        if (!window.confirm(revertPaymentConfirmText({
          amount: p.amount,
          status: p.status,
          flow: 'payment_request',
          sourceType: p.source_type,
          payeeName: task.customer_name,
        }))) return null;
        try {
          const result = await revertPaymentRequestPayment(task.id, p.id);
          showSuccess(prepared ? 'Prepared payment cancelled' : 'Payment reverted');
          return { paidTotal: parseFloat(result.paid_total) };
        } catch (err) {
          showError(err.response?.data?.error || 'Failed to revert payment');
          return null;
        }
      }}
      onSuccess={onSuccess}
      onPartial={onPartial}
    />
  );
}

export default FulfillPaymentRequestModal;
